import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Music, Disc3, Speaker, Check, Sparkles, Armchair, Camera, Video } from "lucide-react";
import { GoldButton, SectionHeading, Reveal } from "../components/ui-kit";
import { SERVICES, PARTNER_SERVICES, FORMULAS, EVENT_TYPES, METHOD_STEPS, IMAGES } from "../data/content";

const ICONS = { Music, Disc3, Speaker, Sparkles, Armchair, Camera, Video };
const SERVICE_ICONS = [Music, Disc3, Speaker];

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-screen flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img src={IMAGES.heroBand} alt="Groupe en live sur scène" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/80 to-background/40" />
          <div className="absolute inset-0 bg-gradient-to-r from-background/90 to-transparent" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 w-full pt-24">
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            className="text-gold uppercase tracking-[0.35em] text-xs sm:text-sm mb-6">
            Prestations événementielles premium
          </motion.p>
          <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.1 }}
            className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[1.05] max-w-4xl">
            Faites de votre événement <span className="gold-text">un moment inoubliable.</span>
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.25 }}
            className="mt-7 text-lg text-foreground/80 max-w-xl leading-relaxed">
            Musique live, DJ, sonorisation et solutions événementielles personnalisées pour créer l'ambiance qui vous ressemble.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.4 }}
            className="mt-10 flex flex-wrap gap-4">
            <GoldButton to="/devis" data-testid="hero-devis-btn">Demander un devis <ArrowRight size={18} /></GoldButton>
            <GoldButton to="/prestations" variant="outline" data-testid="hero-prestations-btn">Découvrir les prestations</GoldButton>
          </motion.div>
        </div>
      </section>

      {/* INTRO */}
      <section className="py-24 sm:py-32 radial-gold">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 text-center">
          <Reveal>
            <p className="text-gold text-xs uppercase tracking-[0.3em] mb-6">KMR Event</p>
            <p className="font-serif text-3xl sm:text-4xl leading-snug text-foreground/90">
              Votre interlocuteur central pour la <span className="gold-text">musique</span>, l'ambiance technique et la coordination de services complémentaires. Un seul contact, une organisation simplifiée.
            </p>
          </Reveal>
        </div>
      </section>

      {/* SERVICES */}
      <section className="py-20 sm:py-28" id="prestations">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal><SectionHeading overline="Nos prestations" title="Ce que KMR Event réalise" subtitle="Des prestations musicales et techniques que nous assurons directement, à combiner selon votre événement." /></Reveal>
          <div className="mt-16 grid gap-6 md:grid-cols-3">
            {SERVICES.map((s, i) => {
              const Icon = SERVICE_ICONS[i];
              return (
                <Reveal key={s.slug} delay={i * 0.1}>
                  <div className="group glass rounded-2xl overflow-hidden h-full flex flex-col hover:border-gold/50 hover:-translate-y-1 transition-all duration-300" data-testid={`service-card-${s.slug}`}>
                    <div className="relative h-52 overflow-hidden">
                      <img src={s.image} alt={s.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                      <div className="absolute inset-0 bg-gradient-to-t from-card to-transparent" />
                      <div className="absolute bottom-4 left-4 glass p-2.5 rounded-full text-gold"><Icon size={22} /></div>
                    </div>
                    <div className="p-7 flex flex-col flex-1">
                      <h3 className="font-serif text-2xl text-foreground">{s.title}</h3>
                      <p className="text-sm text-gold/80 mt-1">{s.tagline}</p>
                      <p className="mt-4 text-sm text-muted-foreground leading-relaxed">{s.short}</p>
                      <ul className="mt-5 space-y-2 flex-1">
                        {s.benefits.map((b) => (
                          <li key={b} className="flex items-center gap-2 text-sm text-foreground/80"><Check size={15} className="text-gold shrink-0" /> {b}</li>
                        ))}
                      </ul>
                      <div className="mt-6 flex items-center justify-between">
                        <Link to={`/${s.slug}`} className="text-sm text-gold hover:underline inline-flex items-center gap-1" data-testid={`service-link-${s.slug}`}>En savoir plus <ArrowRight size={14} /></Link>
                        <Link to="/devis" className="text-sm text-foreground/70 hover:text-gold">Devis</Link>
                      </div>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* FORMULAS */}
      <section className="py-20 sm:py-28 bg-[#0d0d0f]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <SectionHeading overline="Formules combinables" title="Composez la prestation qui vous ressemble" subtitle="Nos services se combinent librement pour créer une formule sur-mesure, adaptée à votre lieu et à votre public." />
            <ul className="mt-8 space-y-3">
              {FORMULAS.map((f) => (
                <li key={f} className="glass rounded-xl px-5 py-4 flex items-center gap-3 hover:border-gold/40 transition-colors" data-testid="formula-item">
                  <span className="text-gold"><Check size={18} /></span>
                  <span className="text-foreground/90">{f}</span>
                </li>
              ))}
            </ul>
            <GoldButton to="/devis" className="mt-8" data-testid="formula-devis-btn">Construire ma formule <ArrowRight size={16} /></GoldButton>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="relative">
              <img src={IMAGES.djMic} alt="Scène et ambiance lumineuse" className="rounded-2xl w-full h-[480px] object-cover gold-glow" />
              <div className="absolute -bottom-6 -left-6 glass rounded-2xl p-6 max-w-[220px] hidden sm:block">
                <p className="font-serif text-3xl gold-text">Sur-mesure</p>
                <p className="text-xs text-muted-foreground mt-1">Chaque devis est unique, adapté à votre événement.</p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* PARTNER SERVICES */}
      <section className="py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal>
            <SectionHeading overline="Nos services partenaires" title="Pour aller plus loin" subtitle="Pour simplifier l'organisation de votre événement, KMR Event peut également vous mettre en relation avec des partenaires spécialisés dans la décoration, la photographie et la vidéo." />
          </Reveal>
          <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {PARTNER_SERVICES.map((p, i) => {
              const Icon = ICONS[p.icon];
              return (
                <Reveal key={p.title} delay={i * 0.08}>
                  <div className="glass rounded-2xl p-7 h-full hover:border-gold/40 hover:-translate-y-1 transition-all duration-300" data-testid="partner-card">
                    <div className="text-gold mb-4"><Icon size={26} /></div>
                    <h3 className="font-serif text-xl text-foreground">{p.title}</h3>
                    <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{p.desc}</p>
                    <span className="inline-block mt-4 text-[11px] uppercase tracking-wider text-gold/70 border border-gold/30 rounded-full px-3 py-1">Partenaire</span>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* EVENT TYPES */}
      <section className="py-20 sm:py-28 bg-[#0d0d0f]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal><SectionHeading overline="Tous vos événements" title="Quel est votre projet ?" subtitle="Sélectionnez votre type d'événement pour démarrer une demande de devis pré-remplie." center /></Reveal>
          <div className="mt-14 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {EVENT_TYPES.map((e, i) => (
              <Reveal key={e.id} delay={i * 0.05}>
                <Link to={`/devis?type=${e.id}`} data-testid={`event-type-${e.id}`}
                  className="glass rounded-xl px-6 py-8 text-center block hover:border-gold/50 hover:bg-gold/5 hover:-translate-y-1 transition-all duration-300">
                  <span className="font-serif text-xl text-foreground">{e.label}</span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* METHOD */}
      <section className="py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal><SectionHeading overline="Notre méthode" title="Un accompagnement simple, du projet au jour J" /></Reveal>
          <div className="mt-14 grid gap-6 md:grid-cols-5">
            {METHOD_STEPS.map((m, i) => (
              <Reveal key={m.n} delay={i * 0.08}>
                <div className="relative h-full">
                  <span className="font-serif text-5xl gold-text opacity-40">{m.n}</span>
                  <h3 className="font-serif text-lg text-foreground mt-2">{m.title}</h3>
                  <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{m.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* GALLERY PREVIEW */}
      <section className="py-20 sm:py-28 bg-[#0d0d0f]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal><SectionHeading overline="Galerie" title="Ambiances & inspirations" subtitle="Un aperçu de l'univers KMR Event. Les visuels réels de nos prestations seront ajoutés prochainement." /></Reveal>
          <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-4">
            {[IMAGES.weddingRoom, IMAGES.djMix, IMAGES.dining, IMAGES.sax].map((img, i) => (
              <Reveal key={img} delay={i * 0.08}>
                <div className={`overflow-hidden rounded-xl ${i % 2 === 0 ? "md:mt-8" : ""}`}>
                  <img src={img} alt="Ambiance événementielle" className="w-full h-56 object-cover hover:scale-105 transition-transform duration-700" />
                </div>
              </Reveal>
            ))}
          </div>
          <div className="text-center mt-12"><GoldButton to="/galerie" variant="outline" data-testid="gallery-more-btn">Voir la galerie</GoldButton></div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-24 sm:py-32 relative overflow-hidden">
        <div className="absolute inset-0"><img src={IMAGES.weddingRoom} alt="" className="w-full h-full object-cover opacity-25" /><div className="absolute inset-0 bg-background/80" /></div>
        <div className="relative z-10 max-w-3xl mx-auto px-5 sm:px-8 text-center">
          <Reveal>
            <h2 className="font-serif text-4xl sm:text-5xl leading-tight">Parlons de votre <span className="gold-text">prochain événement.</span></h2>
            <p className="mt-6 text-lg text-foreground/80 leading-relaxed">
              Décrivez-nous votre projet et recevez une proposition adaptée à votre lieu, votre public et l'ambiance recherchée.
            </p>
            <GoldButton to="/devis" className="mt-10" data-testid="final-cta-btn">Commencer ma demande de devis <ArrowRight size={18} /></GoldButton>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
